import { useEffect, useRef, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "assets/css/MiniCalendar.css";
import ChevronLeft from "components/icons/ChevronLeft";
import ChevronRight from "components/icons/ChevronRight";
import CheckIcon from "components/icons/Check";
import CloseIcon from "components/icons/CloseIcon";
import { Link, useSearchParams } from "react-router-dom";
import DarkButton from "components/actions/DarkButton";
import GrayButton from "components/actions/GrayButton";
import AtriumApi from "api";
import AttendanceConfirmation from "./confirm-dlg";
import WhiteButton from "components/actions/WhiteButton";
import Loader from "components/loader/Loader";
import toast from "react-hot-toast";
import dayjs from "dayjs";

import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const ServiceItem = ({ userService, token, setState }) => {
  const ref = useRef();
  const [showConfirmDlg, setShowConfirmDlg] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startX = 0,
      startY = 0,
      firstMove = true,
      normalScrolling = false;
    const onDragStart = (event) => {
      if (event.touches.length !== 1) {
        return;
      }

      el.classList.remove("transition-all");
      el.classList.add("transition-none");
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
      normalScrolling = false;
      firstMove = true;
    };

    const onDrag = (event) => {
      if (event.touches.length !== 1) {
        return;
      }

      let deltaX = event.touches[0].clientX - startX;
      let deltaY = event.touches[0].clientY - startY;

      if (firstMove && Math.abs(deltaX) < Math.abs(deltaY)) {
        normalScrolling = true;
      }
      firstMove = false;

      if (!normalScrolling) {
        event.preventDefault();
        if (userService.state !== "confirmed" && deltaX > 0) {
          if (deltaX > 100) deltaX = 100;
          el.classList.add("bg-brand-400");
          el.classList.add("dark:bg-brand-700");
          el.classList.remove("bg-red-100");
          el.classList.remove("dark:bg-red-900");
          el.style.marginRight = "" + -deltaX + "px";
          // el.style.marginLeft = '' + (deltaX) + 'px';
          el.children[0].style.width = "" + deltaX + "px";
          el.children[2].style.width = "";
        } else if (userService.state === "na" && deltaX < 0) {
          if (deltaX < -100) deltaX = -100;
          el.classList.add("bg-red-100");
          el.classList.add("dark:bg-red-900");
          el.classList.remove("bg-brand-400");
          el.classList.remove("dark:bg-brand-700");
          // el.style.marginRight = '' + (-deltaX) + 'px';
          el.style.marginLeft = "" + deltaX + "px";
          el.children[0].style.width = "";
          el.children[2].style.width = "" + -deltaX + "px";
        }
      }
    };

    const onDragEnd = (event) => {
      if (!normalScrolling) {
        const resetSlide = () => {
          el.classList.remove("transition-none");
          el.classList.add("transition-all");
          el.style.marginLeft = "";
          el.style.marginRight = "";
          el.children[0].style.width = "";
          el.children[2].style.width = "";
        };

        let deltaX = event.changedTouches[0].clientX - startX;
        if (deltaX >= 100) {
          if (userService.state !== "confirmed") {
            setShowConfirmDlg(true);
            resetSlide();
          }
        } else if (deltaX <= -100) {
          if (userService.state === "na") {
            setState(userService.id, "declined").then(() => resetSlide());
          }
        } else {
          resetSlide();
        }
      }
    };

    el.addEventListener("touchstart", onDragStart);
    el.addEventListener("touchmove", onDrag);
    el.addEventListener("touchend", onDragEnd);
    el.addEventListener("touchcancel", onDragEnd);
    return () => {
      el.removeEventListener("touchstart", onDragStart);
      el.removeEventListener("touchmove", onDrag);
      el.removeEventListener("touchend", onDragEnd);
      el.removeEventListener("touchcancel", onDragEnd);
    };
  }, [ref, setState, userService]);

  let dateString;
  const localDate = dayjs.utc(userService.service.date).local();

  if (userService.service.schedule === "weekly") {
    dateString = "Every " + localDate.format("dddd");
  } else {
    dateString = localDate.format("MMMM D, dddd");
  }

  const showButtons =
    userService?.service?.schedule === "weekly" ||
    (userService?.service?.schedule === "once" &&
      dayjs(
        `${userService?.service?.date.split("T")[0]}T${
          userService?.service?.startAt
        }`
      ).diff(dayjs(), "minute") >= 60);

  return (
    <div className="w-full overflow-x-hidden px-4">
      <div className="w-full rounded-3xl mb-4">
        <div
          className="flex flex-row bg-red-100 select-none rounded-3xl items-center content-center transition-none duration-300 ease-in-out"
          ref={ref}
        >
          <div className="w-0 overflow-hidden text-center content-center">
            <div className="w-24 h-full">
              <div className="h-full flex w-10 h-10 m-auto">
                <span className="inline-block m-auto bg-brand-800 dark:bg-brand-600 p-2 text-white rounded-full">
                  <CheckIcon />
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 p-5 bg-white dark:bg-navy-800 rounded-2xl flex flex-row gap-4 items-center">
            <div
              className={`flex-1 border-s-4 pl-3 ${
                userService.state === "confirmed"
                  ? "border-brand-500"
                  : userService.state === "declined"
                  ? "border-red-500"
                  : "border-gray-500"
              }`}
            >
              <p className="text-xl font-bold">{userService.service.name}</p>
              <p className="text-sm text-gray-700 font-medium">{dateString}</p>
              <p className="text-sm text-gray-700 font-medium break-all">
                {new Date(
                  "2000-01-01 " + userService.service.startAt
                ).toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "numeric",
                })}
                &nbsp;-&nbsp;
                {new Date(
                  "2000-01-01 " + userService.service.endAt
                ).toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "numeric",
                })}
                &nbsp;@{userService?.service?.address}
              </p>
            </div>
            {showButtons && (
              <div className="hidden lg:flex lg:flex-row lg:gap-3">
                {userService.state === "na" && (
                  <DarkButton onClick={() => setShowConfirmDlg(true)}>
                    Confirm
                  </DarkButton>
                )}
                {userService.state === "confirmed" && (
                  <GrayButton onClick={() => setState(userService.id, "na")}>
                    Cancel confirmation
                  </GrayButton>
                )}
                {userService.state === "na" && (
                  <GrayButton
                    onClick={() => setState(userService.id, "declined")}
                  >
                    Decline
                  </GrayButton>
                )}
                {userService.state === "declined" && (
                  <GrayButton onClick={() => setState(userService.id, "na")}>
                    Cancel decline
                  </GrayButton>
                )}
              </div>
            )}

            {showConfirmDlg && (
              <AttendanceConfirmation
                userService={userService}
                onConfirm={() =>
                  setState(userService.id, "confirmed").then(() =>
                    setShowConfirmDlg(false)
                  )
                }
                onCancel={() => setShowConfirmDlg(false)}
              />
            )}

            <div className="items-center">
              <Link
                to={`/volunteer/services/${userService.id}?token=${token}`}
                className="inline-block text-gray-800 bg-gray-50 dark:bg-navy-700 dark:text-white p-3 rounded-2xl"
              >
                <ChevronRight />
              </Link>
            </div>
          </div>

          <div className="w-0 overflow-hidden text-center content-center">
            <div className="w-24 h-full">
              <div className="h-full flex w-10 h-10 m-auto">
                <span className="inline-block m-auto bg-red-500 p-3 text-white rounded-full">
                  <CloseIcon />
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VolunteerServices = () => {
  const [date, setDate] = useState();
  const [queryParams] = useSearchParams();
  const token = queryParams.get("token");
  const [services, setServices] = useState([]);
  const [user, setUser] = useState({});
  const [justConfirmed, setJustConfirmed] = useState(false);
  const [justDeclined, setJustDeclined] = useState(false);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = { token };
    if (date) params.date = date.toLocaleDateString(["en-US"]);

    setLoading(true);
    AtriumApi.get("/v1/volunteer/services", { params: params })
      .then((resp) => {
        setUser(resp?.data?.user);
        setServices(resp?.data?.services);
      })
      .catch((error) => {
        console.log("error", error);
      })
      .finally(() => setLoading(false));
  }, [token, date]);

  let tout;
  const setState = (userServiceId, newState) => {
    setJustConfirmed(false);
    setJustDeclined(false);
    clearTimeout(tout);
    return AtriumApi.put(
      `/v1/volunteer/services/${userServiceId}`,
      { state: newState },
      { params: { token } }
    )
      .then((resp) => {
        const userService = resp?.data;
        const index = services?.findIndex((m) => m.id === userService.id);
        if (index !== -1) {
          setServices(
            services
              .slice(0, index)
              .concat([userService])
              .concat(services.slice(index + 1))
          );
        }
        if (resp.data.state === "confirmed") {
          setJustConfirmed(true);
          tout = setTimeout(() => {
            setJustConfirmed(false);
          }, 1500);
        } else if (resp.data.state === "declined") {
          setJustDeclined(true);
          tout = setTimeout(() => {
            setJustDeclined(false);
          }, 1500);
        }
      })
      .catch((error) => {
        toast.error(error?.data?.message || "An error occurred.");
      });
  };

  return (
    <div className="mb-8">
      <h1 className="text-3xl mx-4 font-medium my-4">
        <span className="inline-block">Service requests for&nbsp;</span>
        <span className="inline-block text-nowrap font-bold">
          {user.firstName} {user.lastName}
        </span>
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 md:p-0">
        {/* Services section */}
        <div className="order-2 lg:order-1 lg:col-span-8">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : (
            <>
              {services?.length ? (
                <div>
                  <h2 className="font-bold text-xl mx-4 h-10">
                    Your services
                    {date && (
                      <>
                        &nbsp;<span>on</span>&nbsp;
                        <span className="text-brand-600">
                          {date.toLocaleDateString([], {
                            month: "long",
                            day: "numeric",
                          })}
                        </span>
                      </>
                    )}
                  </h2>

                  {services.map((service, i) => (
                    <ServiceItem
                      key={"service" + i}
                      token={token}
                      userService={service}
                      setState={setState}
                    />
                  ))}
                  {date && services?.length && (
                    <div className="flex flex-col max-w-[1080px] mx-auto p-4">
                      <WhiteButton onClick={() => setDate(null)}>
                        Show all
                      </WhiteButton>
                    </div>
                  )}
                </div>
              ) : (
                <h2 className="font-bold text-xl mx-4 h-10 text-center py-10">
                  No services
                  {date && (
                    <>
                      &nbsp;<span>on</span>&nbsp;
                      <span className="text-brand-600">
                        {date.toLocaleDateString([], {
                          month: "long",
                          day: "numeric",
                        })}
                      </span>
                    </>
                  )}
                </h2>
              )}
            </>
          )}
        </div>

        {/* Calendar section */}
        <div className="order-1 lg:order-2 lg:col-span-4">
          <div className="w-full bg-white dark:bg-navy-800 p-4 mx-auto rounded-2xl sm:max-w-[320px]">
            <Calendar
              onChange={(v) => {
                setDate(v);
                setJustConfirmed(false);
              }}
              value={date}
              prevLabel={<ChevronLeft />}
              nextLabel={<ChevronRight />}
            />
          </div>
        </div>
      </div>

      {justConfirmed && (
        <div className="fixed bottom-0 left-0 w-full">
          <div className="w-full max-w-[1080px] mx-auto p-4">
            <div className="bg-navy-800 text-white dark:bg-navy-50 dark:text-gray-800 p-4 rounded-2xl flex flex-row gap-2 items-center">
              <span className="inline-block align-middle bg-brand-500 p-1 text-white rounded-full">
                <CheckIcon />
              </span>
              <span className="flex-1">Attendance confirmed</span>
              {/* <button>Undo</button> */}
            </div>
          </div>
        </div>
      )}

      {justDeclined && (
        <div className="fixed bottom-0 left-0 w-full">
          <div className="w-full max-w-[1080px] mx-auto p-4">
            <div className="bg-navy-800 text-white dark:bg-navy-50 dark:text-gray-800 p-4 rounded-2xl flex flex-row gap-2 items-center">
              <span className="inline-block align-middle bg-red-500 p-1 text-white rounded-full">
                <CloseIcon />
              </span>
              <span className="flex-1">Attendance declined</span>
              {/* <button>Undo</button> */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VolunteerServices;
